import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarPlus, Eye, MessageSquare, User as UserIcon } from 'lucide-react';
import { publicRoleLabel } from '../utils/roles';
import ConnectMemberModal from './ConnectMemberModal';
import RequestMeetingModal from './RequestMeetingModal';

export type AskIfMember = {
  id: number;
  full_name?: string;
  name?: string;
  avatar_url?: string;
  role?: string;
  chapter?: string;
  industry?: string;
  title?: string;
  bio?: string;
  areas_of_expertise?: string[];
  matched_on?: string[];
  match_reason?: string;
};

const INITIAL_VISIBLE = 5;

type Props = {
  members: AskIfMember[];
  onPick?: (member: AskIfMember) => void;
  pickLabel?: string;
};

export function normalizeAskIfMember(raw: AskIfMember): AskIfMember {
  return {
    ...raw,
    full_name: raw.full_name || raw.name || 'Member',
  };
}

export function membersFromAskIfAction(action?: {
  type?: string;
  users?: AskIfMember[];
  data?: unknown;
} | null): AskIfMember[] {
  if (!action) return [];
  const raw = Array.isArray(action.users)
    ? action.users
    : Array.isArray(action.data)
      ? action.data
      : [];
  const seen = new Set<number>();
  const members: AskIfMember[] = [];
  for (const row of raw as AskIfMember[]) {
    if (!row?.id || seen.has(row.id)) continue;
    seen.add(row.id);
    members.push(normalizeAskIfMember(row));
  }
  return members;
}

export default function AskIfMemberCards({ members, onPick, pickLabel }: Props) {
  const [connectMember, setConnectMember] = useState<AskIfMember | null>(null);
  const [meetMember, setMeetMember] = useState<AskIfMember | null>(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const people = members.map(normalizeAskIfMember);
  if (people.length === 0) return null;
  const visible = people.slice(0, visibleCount);
  const hidden = people.length - visible.length;

  return (
    <>
      <div className="mt-3 space-y-2">
        {visible.map((member) => (
          <div
            key={member.id}
            className="bg-white border border-gray-200 rounded-xl p-3 text-left"
          >
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-full overflow-hidden bg-off-white flex items-center justify-center shrink-0">
                {member.avatar_url ? (
                  <img src={member.avatar_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <UserIcon size={20} className="text-navy" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-navy truncate">{member.full_name}</p>
                <p className="text-xs text-gray-500 truncate">
                  {[publicRoleLabel(member.role), member.title || member.industry, member.chapter]
                    .filter(Boolean)
                    .join(' • ')}
                </p>
                {member.match_reason && (
                  <p className="text-xs text-navy mt-1.5 font-medium">{member.match_reason}</p>
                )}
                {(member.matched_on || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {member.matched_on!.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex px-2 py-0.5 rounded-full bg-gold/15 text-navy text-[11px] font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                {member.bio && (
                  <p className="text-xs text-gray-600 mt-1 line-clamp-2">{member.bio}</p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <Link
                to={`/members/${member.id}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-navy hover:border-navy/40"
              >
                <Eye size={13} />
                Profile
              </Link>
              {onPick ? (
                <button
                  type="button"
                  onClick={() => onPick(member)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-navy text-white hover:bg-navy-dark"
                >
                  {pickLabel || 'Select'}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setConnectMember(member)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-navy hover:border-navy/40"
                  >
                    <MessageSquare size={13} />
                    Message
                  </button>
                  <button
                    type="button"
                    onClick={() => setMeetMember(member)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-navy hover:border-navy/40"
                  >
                    <CalendarPlus size={13} />
                    Meet
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
        {hidden > 0 && (
          <button
            type="button"
            onClick={() => setVisibleCount(people.length)}
            className="w-full py-2 text-xs font-medium text-navy hover:text-gold"
          >
            Show {hidden} more
          </button>
        )}
      </div>
      {connectMember && (
        <ConnectMemberModal
          member={{
            id: connectMember.id,
            full_name: connectMember.full_name || 'Member',
            avatar_url: connectMember.avatar_url,
            role: connectMember.role,
            chapter: connectMember.chapter,
          }}
          onClose={() => setConnectMember(null)}
        />
      )}
      {meetMember && (
        <RequestMeetingModal
          inviteeId={meetMember.id}
          inviteeName={meetMember.full_name || 'Member'}
          onClose={() => setMeetMember(null)}
        />
      )}
    </>
  );
}
